/**
Problem Details (RFC 7807 style) + validation bag
*/
interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, unknown>; // may be string|string[]|unknown
}

/**
Narrower shape for Axios-like errors (without importing axios types)
*/
interface AxiosLikeError {
  message?: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const asStringArray = (v: unknown): string[] | null => {
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) return v as string[];
  return typeof v === "string" ? [v] : null;
};

/**
 * Flattens `errors` bag into a list of messages.
 * Example input:
 * {
 *   "Validation.Job.InvalidNameFormat": [
 *      "A Job name must consist of only alphanumeric characters, '-', '_', or '.', and must start and end with an alphanumeric character."
 *   ],
 *   "another.key": "Single string message"
 * }
 */
const flattenErrorBag = (errors: Record<string, unknown>): string[] => {
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    const array = asStringArray(value);
    if (array) {
      // We keep backend-provided messages as-is. You can map keys to friendly labels if desired.
      messages.push(...array);
    }
  }
  return messages;
};

export const extractProblemDetails = (error: unknown): { title?: string; status?: number; detail?: string; messages: string[] } => {
  const ax = error as AxiosLikeError | undefined;
  const data = ax?.response?.data;

  // Default envelope
  const result = {
    title: undefined as string | undefined,
    status: ax?.response?.status,
    detail: undefined as string | undefined,
    messages: [] as string[],
  };

  if (isObject(data)) {
    // Try to treat it as ProblemDetails
    const pd = data as ProblemDetails;

    if (typeof pd.title === "string") result.title = pd.title;
    if (typeof pd.status === "number") result.status = pd.status;
    if (typeof pd.detail === "string" && pd.detail.trim()) result.detail = pd.detail;

    if (isObject(pd.errors)) {
      result.messages = flattenErrorBag(pd.errors);
    }
  }

  // Fallbacks
  if (!result.title) {
    // Some APIs put main message in response.data.message
    if (isObject(data) && typeof data.message === "string") result.title = data.message;
    else if (typeof ax?.message === "string") result.title = ax.message;
  }

  return result;
};

export const getUploadErrorMessage = (error: unknown): string => {
  const { detail, title, messages, status } = extractProblemDetails(error);
  const text = [...new Set([detail ?? title, ...messages].filter((message): message is string => Boolean(message)))].join("\n");
  return `${text || "The upload could not be completed. Please try again."}${status ? ` (HTTP ${status})` : ""}`;
};
